import { useEffect, useReducer } from 'react';

export const dcfInitialState = {
    baseRevenue: null,
    stage1Years: 5,
    stage1Growth: 0.05,
    stage2Years: 5,
    stage2Growth: 0.03,
    fcfGrowthRate: 0.05,
    startingFcf: null,
    operatingMarginStart: 0.15,
    operatingMarginTarget: 0.15,
    taxRate: 0.21,
    dAndAPctRevenue: 0.03,
    capexPctRevenue: 0.04,
    nwcPctRevenueChange: 0.02,
    wacc: 0.09,
    terminalGrowth: 0.025,
    forecastYears: 5,
    netDebt: null,
    sharesOutstanding: null,
    seeded: false,
};

function finiteOrFallback(value, fallback) {
    if (value == null || value === '') return fallback;
    return Number.isFinite(Number(value)) ? Number(value) : fallback;
}

export function dcfReducer(state, action) {
    switch (action.type) {
        case 'seed_defaults': {
            if (state.seeded && !action.force) return state;
            const growth = finiteOrFallback(action.defaults?.revenue_growth, state.stage1Growth);
            const margin = finiteOrFallback(action.defaults?.operating_margin, state.operatingMarginStart);
            return {
                ...state,
                baseRevenue: finiteOrFallback(action.baseRevenue?.value, state.baseRevenue),
                stage1Growth: growth,
                stage2Growth: growth,
                fcfGrowthRate: growth,
                operatingMarginStart: margin,
                operatingMarginTarget: margin,
                netDebt: finiteOrFallback(action.netDebt?.value, state.netDebt),
                sharesOutstanding: finiteOrFallback(action.shares?.value, state.sharesOutstanding),
                seeded: true,
            };
        }
        case 'set_field':
            if (action.field === 'fcfGrowthRate') {
                return {
                    ...state,
                    fcfGrowthRate: action.value,
                    stage1Growth: action.value,
                    stage2Growth: action.value,
                };
            }
            return { ...state, [action.field]: action.value };
        case 'set_starting_fcf_default':
            if (state.startingFcf != null || !Number.isFinite(Number(action.value))) return state;
            return { ...state, startingFcf: action.value };
        case 'load_scenario': {
            if (!action.assumptions || typeof action.assumptions !== 'object') return state;
            const loaded = {};
            Object.keys(dcfInitialState).forEach((field) => {
                if (field !== 'seeded' && Object.hasOwn(action.assumptions, field)) {
                    loaded[field] = action.assumptions[field];
                }
            });
            if (!Object.hasOwn(loaded, 'fcfGrowthRate') && Object.hasOwn(loaded, 'stage1Growth')) {
                loaded.fcfGrowthRate = loaded.stage1Growth;
            }
            return { ...state, ...loaded, seeded: true };
        }
        case 'apply_average':
            if (!Number.isFinite(action.value)) return state;
            if (action.driver === 'revenue_growth') {
                return {
                    ...state,
                    stage1Growth: action.value,
                    stage2Growth: action.value,
                    fcfGrowthRate: action.value,
                };
            }
            if (action.driver === 'operating_margin') {
                return {
                    ...state,
                    operatingMarginStart: action.value,
                    operatingMarginTarget: action.value,
                };
            }
            return state;
        default:
            return state;
    }
}

export function useDcfController({ defaults, baseRevenue, netDebt, shares }) {
    const [state, dispatch] = useReducer(dcfReducer, dcfInitialState);

    useEffect(() => {
        const hasDefaults = defaults && Object.keys(defaults).length > 0;
        const hasSourceValue = [baseRevenue, netDebt, shares]
            .some((source) => source?.value != null);
        if (!hasDefaults && !hasSourceValue) return;
        dispatch({
            type: 'seed_defaults',
            defaults,
            baseRevenue,
            netDebt,
            shares,
        });
    }, [defaults, baseRevenue, netDebt, shares]);

    return { state, dispatch };
}
